import { useMemo, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { useVisibleStore } from './useVisibleStore';
import { findBestTeamByEmail } from '../lib/teamUtils';

/**
 * Shared message logic for MessagesView and MessagingDrawer.
 * Extracts all filtering, merging, unread-count, and sorting logic
 * so fixes apply to both components automatically.
 */
export function useMessages() {
  const { currentUser, categories } = useVisibleStore();
  const rawMessages = useStore(state => state.messages);
  const rawTeam = useStore(state => state.team);
  const teamList = useVisibleStore().team;

  const isAdmin = currentUser.role === 'admin';
  const isManager = currentUser.role === 'manager';

  // ── My team IDs (all records sharing my email) ──
  const myEmail = currentUser.email || (typeof window !== 'undefined' ? localStorage.getItem('wfm_user_email') : null);
  const myTeamIds = useMemo(() => new Set(
    myEmail
      ? rawTeam.filter(s => s.email?.toLowerCase().trim() === myEmail.toLowerCase().trim()).map(s => s.id)
      : []
  ), [rawTeam, myEmail]);

  // ── Relevant messages (only those addressed to me or from my team) ──
  // Build set of my team names for detecting messages sent BY me as admin
  const myNames = useMemo(() => new Set(
    rawTeam.filter(s => myTeamIds.has(s.id)).map(s => s.name.toLowerCase().trim())
  ), [rawTeam, myTeamIds]);

  const relevantMessages = useMemo(() => (rawMessages || []).filter(m => {
    const isMyTeam = myTeamIds.has(m.teamId);
    // Broadcast messages (no recipient, special sender labels) are addressed to all admins
    const isBroadcast = !m.recipientTeamId && ((m.senderName || '').startsWith('🚨') || (m.senderName || '').startsWith('📢'));
    const isAddressedToMe = isBroadcast || (!!m.recipientTeamId && myTeamIds.has(m.recipientTeamId));
    // Messages sent BY me as admin: senderName contains one of my team names
    const sentByMe = !m.fromTeam && [...myNames].some(n => n && (m.senderName || '').toLowerCase().includes(n));
    return (teamList.some(t => t.id === m.teamId) || isMyTeam || isAddressedToMe || sentByMe)
        && (isAddressedToMe || isMyTeam || sentByMe);
  }), [rawMessages, myTeamIds, teamList, myNames]);

  // ── Messages grouped by team ID ──
  const messagesByTeam = useMemo(() => {
    const byTeam: Record<string, typeof relevantMessages> = {};
    relevantMessages.forEach(m => {
      if (!byTeam[m.teamId]) byTeam[m.teamId] = [];
      byTeam[m.teamId].push(m);
    });
    Object.keys(byTeam).forEach(id => {
      byTeam[id].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    });
    return byTeam;
  }, [relevantMessages]);

  // ── Admin->me messages merged by sender name (helper) ──
  const getAdminToMeMessages = useCallback((teamName: string) => {
    return relevantMessages.filter(m =>
      myTeamIds.has(m.teamId) && !m.fromTeam && (m.senderName || '').includes(teamName)
    );
  }, [relevantMessages, myTeamIds]);

  // ── Filtered contacts (exclude my own teams) ──
  const filteredTeam = useMemo(() => teamList.filter(s => {
    if (myTeamIds.has(s.id)) return false;
    if (isManager && currentUser.categoryId && s.categoryId !== currentUser.categoryId) return false;
    return true;
  }), [teamList, myTeamIds, isManager, currentUser.categoryId]);

  // ── Unread count for a specific team ──
  const unreadCountForTeam = useCallback((teamId: string) => {
    const team = teamList.find(s => s.id === teamId);
    const direct = (messagesByTeam[teamId] || []).filter(m => !m.read && m.fromTeam);
    // Exclude broadcasts from per-contact unread count
    const isBroadcast = (m: any) => (m.senderName || '').startsWith('🚨') || (m.senderName || '').startsWith('📢');
    const adminToMe = team ? getAdminToMeMessages(team.name).filter(m => !m.read && !isSentByMe(m) && !isBroadcast(m)) : [];
    return direct.length + adminToMe.length;
  }, [messagesByTeam, teamList, getAdminToMeMessages]);

  // ── Last message + unread indicator for a contact card ──
  const getContactPreview = useCallback((teamId: string, teamName: string) => {
    const direct = messagesByTeam[teamId] || [];
    const adminToMe = getAdminToMeMessages(teamName);
    const all = [...direct, ...adminToMe].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    const lastMsg = all[0] || null;
    // Broadcasts (🚨/📢) are global, not per-contact — don't count as unread for individual cards
    const hasUnread = all.some(m => !m.read && !isSentByMe(m) && !(m.fromTeam && m.senderName === teamName) && (!!m.recipientTeamId && myTeamIds.has(m.recipientTeamId)));
    return { lastMsg, hasUnread, all };
  }, [messagesByTeam, getAdminToMeMessages, myTeamIds]);

  // ── Messages for a selected conversation (merged) ──
  const getActiveMessages = useCallback((teamId: string, teamName: string) => {
    const direct = messagesByTeam[teamId] || [];
    const adminToMe = getAdminToMeMessages(teamName);
    const all = [...direct, ...adminToMe];
    const seen = new Set<string>();
    return all.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [messagesByTeam, getAdminToMeMessages]);

  // ── Helper: is this message sent BY me as admin? ──
  const isSentByMe = useCallback((m: { fromTeam?: boolean; senderName?: string }) => {
    if (m.fromTeam) return false;
    return [...myNames].some(n => n && (m.senderName || '').toLowerCase().includes(n));
  }, [myNames]);

  // ── Total unread count (exclude messages I sent myself) ──
  const totalUnread = useMemo(() =>
    relevantMessages.filter(m => !m.read && !isSentByMe(m) && (m.fromTeam || myTeamIds.has(m.teamId))).length,
    [relevantMessages, myTeamIds, isSentByMe]
  );

  // ── Email validation helper ──
  const isEmailValid = useCallback((teamMember: { email?: string; emailConfirmed?: boolean }) => {
    return !!(teamMember.email && teamMember.email.includes('@') && teamMember.emailConfirmed);
  }, []);

  // ── Sorted contacts (unread first, then valid email, then most recent) ──
  const sortedTeam = useMemo(() =>
    [...filteredTeam].sort((a, b) => {
      const unreadA = unreadCountForTeam(a.id);
      const unreadB = unreadCountForTeam(b.id);
      if (unreadA !== unreadB) return unreadB - unreadA;
      // Valid emails first, invalid ones at the bottom
      const validA = isEmailValid(a) ? 1 : 0;
      const validB = isEmailValid(b) ? 1 : 0;
      if (validA !== validB) return validB - validA;
      const { lastMsg: lastA } = getContactPreview(a.id, a.name);
      const { lastMsg: lastB } = getContactPreview(b.id, b.name);
      const tA = lastA ? new Date(lastA.timestamp).getTime() : 0;
      const tB = lastB ? new Date(lastB.timestamp).getTime() : 0;
      return tB - tA;
    }), [filteredTeam, unreadCountForTeam, getContactPreview, isEmailValid]
  );

  // ── Sender label for the current admin/manager ──
  const getSenderLabel = useCallback(() => {
    const teamMember = findBestTeamByEmail(teamList, myEmail);
    if (currentUser.role === 'admin') return teamMember ? `Admin : ${teamMember.name}` : 'Admin';
    if (currentUser.role === 'manager') {
      if (teamMember) return `Manager : ${teamMember.name}`;
      const cat = categories.find(c => c.id === currentUser.categoryId);
      return cat ? `Manager : ${cat.name}` : 'Manager';
    }
    return 'Admin';
  }, [teamList, myEmail, currentUser.role, currentUser.categoryId, categories]);

  return {
    // Data
    myEmail, myTeamIds, rawMessages, rawTeam, teamList,
    relevantMessages, messagesByTeam,
    filteredTeam, sortedTeam,
    totalUnread,
    isAdmin, isManager,
    // Functions
    isEmailValid,
    unreadCountForTeam,
    getContactPreview,
    getActiveMessages,
    getAdminToMeMessages,
    getSenderLabel,
  };
}
